import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { Col, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Button } from 'ypf-pas-general-ui-components';
import { AiOutlineMinusCircle } from 'react-icons/ai';
import { useLocation } from 'react-router-dom';

import AmountInput from '../AmountInput/AmountInput';
import LinePositionName from '../LinePositionName/LinePositionName';
import ModalLineMenu from '../ModalLineMenu/ModalLIneMenu';
import DeleteLineModal from '../DeleteLineModal';
import { Line } from '../../../utils/globalPropTypes';
import {
  AttachedFilesPermissions,
  PositionLinePermissions
} from '../../MonthlyServiceRequest/RequestDetailTypes';
import useCloseModalLineMenu from '../../../hooks/useCloseModalLineMenu';
import ActionsButtons from '../ActionsButtons/ActionsButtons';
import { validateOnlyDecimalInput } from '../../../utils/formatUtils';
import { checkRol } from '../../../security';
import { notificador, proveedorNotificador } from '../../../security/constantsRols';

const renderTooltipContent = (line) => {
  const locationStr = line?.ubicacionStr.toUpperCase();
  const finalizadaStr = line?.finalizada ? ' (Serv. Finalizado)' : '';

  return `${locationStr}${finalizadaStr}`;
};

const getLineIdentifier = (line, monthly) => (monthly ? line?.idUbicacionLinea : line.idParteLinea);

const getIdWorkPart = (line, monthly) => (!monthly ? line.idPartetrabajo : null);

const LineRowContainerGeneric = (props) => {
  const {
    changeInLines,
    enableNotification,
    indexLine,
    isOyM,
    line,
    newFetch,
    selectedDate,
    setChangeInLines,
    setNewFetch,
    positionLinesPermissions,
    typeOfUbication,
    disabledEditStateCancelOrFinish,
    asignationType,
    setWorkReportLines,
    fetchToApproveALine,
    startDate,
    templateComponent,
    disabledInput,
    forceWindow,
    monthly,
    monthlyPositionLine,
    deleteTemplateLines,
    filesPermissions,
    notificaPP,
    maxDate,
    minDate,
    idLocation
  } = props;

  const { pathname } = useLocation();

  const [, , idSolicitud] = pathname.split('/');

  const { setFieldValue, values } = useFormikContext();

  const { wrapperRef, showModalMenu, setShowModalMenu } = useCloseModalLineMenu();

  const [amount, setAmount] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isInputDisabled, setIsInputDisabled] = useState();
  const [isRectifyAmount, setIsRectifyAmount] = useState(false);
  const isProviderOrNotifier = checkRol(proveedorNotificador) || checkRol(notificador);
  const showLineBlockedBySap = line?.bloqueadoEnSap && isProviderOrNotifier;

  const onChange = (val) => {
    const { value } = val.target;

    if (validateOnlyDecimalInput(value, 3)) {
      setFieldValue(`line[${indexLine}].cantidad`, value);
    } else if (value === '') {
      setFieldValue(`line[${indexLine}].cantidad`, null);
    }
    setChangeInLines(!changeInLines);
  };

  useEffect(() => {
    setAmount(typeof line?.cantidad === 'number' ? parseFloat(line?.cantidad) : line?.cantidad);
  }, [line]);

  useEffect(() => {
    setIsInputDisabled(!enableNotification);
  }, [enableNotification]);

  return (
    <>
      <div className="line_row_wrapper">
        <Row className="d-flex align-items-center justify-content-around">
          {monthlyPositionLine ? (
            <Col
              className="postionLineLabel"
              sm={8}
              style={{
                cursor: 'default'
              }}
            >
              <OverlayTrigger
                className="postionLineLabel w-10"
                delay={{
                  show: 250,
                  hide: 400
                }}
                overlay={
                  <Tooltip id="button-tooltip">
                    <span>{renderTooltipContent(line)}</span>
                  </Tooltip>
                }
                placement="top"
              >
                <span>{line?.ubicacionStr.toUpperCase()}</span>
              </OverlayTrigger>
            </Col>
          ) : (
            <>
              <LinePositionName
                columnNumber={3}
                description={line.descripcionPosicionYClic}
                filesPermissions={filesPermissions}
                idLocation={idLocation}
                idWorkLine={getLineIdentifier(line, monthly)}
                idWorkPart={getIdWorkPart(line, monthly)}
                lineBlockedBySap={showLineBlockedBySap}
                lineId={monthly ? line?.idUbicacionLinea : line.idParteLinea}
                monthly={monthly}
                name={line.positionName}
              />
              <LinePositionName
                columnNumber={3}
                description={line.descripcionLineaYClic}
                filesPermissions={filesPermissions}
                idLocation={idLocation}
                idWorkLine={getLineIdentifier(line, monthly)}
                idWorkPart={getIdWorkPart(line, monthly)}
                lineBlockedBySap={showLineBlockedBySap}
                lineId={line.idParteLinea}
                monthly={monthly}
                name={line.lineName}
              />
              <Col sm={1}>
                <span className={`${showLineBlockedBySap ? 'text-danger' : ''}`}>
                  {line.unidadMedida}
                </span>
              </Col>
              {templateComponent && (
                <div className="col-sm-4 d-flex align-items-center justify-content-around">
                  <Col className="d-flex align-items-center" sm={2}>
                    {Number.isNaN(amount) ? '-' : amount}
                  </Col>
                  <Col className="d-flex" sm={1}>
                    {line.onCall ? 'Sí' : 'No'}
                  </Col>
                  <Col className="d-flex" sm={1}>
                    {line.mensualizada ? 'Sí' : 'No'}
                  </Col>
                  <Col className="d-flex" sm={1}>
                    <Button
                      colorIcon="danger"
                      disabled={line.length === 1}
                      icon={AiOutlineMinusCircle}
                      onClick={() => deleteTemplateLines(indexLine)}
                      sizeIcon="24px"
                      type="button"
                      variant="outline"
                      widthButton="40px"
                    />
                  </Col>
                </div>
              )}
            </>
          )}
          {!templateComponent && (
            <>
              <Col sm={2}>
                <AmountInput
                  disabled={
                    disabledInput || isInputDisabled || line?.finalizada || showLineBlockedBySap
                  }
                  disabledEditStateCancelOrFinish={disabledEditStateCancelOrFinish}
                  isOyM={isOyM}
                  line={line}
                  notificaPP={notificaPP}
                  onChange={onChange}
                  typeOfUbication={typeOfUbication}
                  value={line.cantidad || values?.line[indexLine].cantidad}
                />
              </Col>
              <Col ref={wrapperRef} sm={2}>
                <ActionsButtons
                  amount={amount}
                  asignationType={asignationType}
                  canAttachFiles={positionLinesPermissions.attachedFiles.write}
                  canSendComments={
                    positionLinesPermissions.comments.write &&
                    !disabledEditStateCancelOrFinish &&
                    !showLineBlockedBySap
                  }
                  canSendToApprove={positionLinesPermissions.sendToApprove}
                  canVisualizeComments={positionLinesPermissions.comments.visualize}
                  canVisualizeFiles={positionLinesPermissions.attachedFiles.visualize}
                  changeInLines={changeInLines}
                  disabledEditStateCancelOrFinish={disabledEditStateCancelOrFinish}
                  disabledPPModal={isInputDisabled || line.flujoMobile}
                  expertReview={positionLinesPermissions?.expertReview.actionInspectorButton}
                  fetchToApproveALine={fetchToApproveALine}
                  forceWindow={forceWindow}
                  isRectifyAmount={isRectifyAmount}
                  line={{
                    ...line,
                    idSolicitud: parseInt(idSolicitud, 10),
                    tipoAsignacion: asignationType,
                    bloqueadoEnSap: showLineBlockedBySap
                  }}
                  maxDate={maxDate}
                  minDate={minDate}
                  monthly={monthly}
                  newFetch={newFetch}
                  notificaPP={notificaPP}
                  positionLinesPermissions={positionLinesPermissions}
                  selectedDate={selectedDate}
                  setAmount={setAmount}
                  setChangeInLines={setChangeInLines}
                  setIsRectifyAmount={setIsRectifyAmount}
                  setNewFetch={setNewFetch}
                  setShowModalMenu={setShowModalMenu}
                  showModalMenu={showModalMenu}
                  startDate={startDate}
                  typeOfUbication={typeOfUbication}
                />
              </Col>
            </>
          )}
        </Row>
        <ModalLineMenu
          amount={amount}
          disabled={disabledEditStateCancelOrFinish}
          disabledDeleteAction={
            !positionLinesPermissions.deleteLine || disabledEditStateCancelOrFinish
          }
          disabledPPModal={isInputDisabled}
          disabledRectifyLine={!positionLinesPermissions.rectifyLine}
          filesPermissions={filesPermissions}
          idLocation={idLocation}
          isRectifyAmount={isRectifyAmount}
          line={line}
          monthly={monthly}
          selectedDate={selectedDate}
          setAmount={setAmount}
          setIsRectifyAmount={setIsRectifyAmount}
          setNewFetch={setNewFetch}
          setShowDeleteModal={setShowDeleteModal}
          setShowModalMenu={setShowModalMenu}
          setWorkReportLines={setWorkReportLines}
          showModalMenu={showModalMenu}
        />
      </div>
      <DeleteLineModal
        isMonthly={monthly}
        line={line}
        setNewFetch={setNewFetch}
        setShowDeleteModal={setShowDeleteModal}
        showDeleteModal={showDeleteModal}
      />
    </>
  );
};

LineRowContainerGeneric.propTypes = {
  enableNotification: PropTypes.bool.isRequired,
  indexLine: PropTypes.number.isRequired,
  isOyM: PropTypes.bool,
  line: Line.isRequired,
  selectedDate: PropTypes.string.isRequired,
  changeInLines: PropTypes.bool.isRequired,
  setChangeInLines: PropTypes.func,
  newFetch: PropTypes.bool.isRequired,
  setNewFetch: PropTypes.func.isRequired,
  typeOfUbication: PropTypes.shape({
    id: PropTypes.number.isRequired,
    nombre: PropTypes.string.isRequired
  }).isRequired,
  disabledEditStateCancelOrFinish: PropTypes.bool.isRequired,
  disabledInput: PropTypes.bool,
  fetchToApproveALine: PropTypes.func,
  positionLinesPermissions: PositionLinePermissions.isRequired,
  setWorkReportLines: PropTypes.func.isRequired,
  asignationType: PropTypes.number,
  startDate: PropTypes.string,
  templateComponent: PropTypes.bool,
  forceWindow: PropTypes.bool,
  maxDate: PropTypes.string,
  minDate: PropTypes.string,
  monthly: PropTypes.bool,
  monthlyPositionLine: PropTypes.bool,
  deleteTemplateLines: PropTypes.func,
  filesPermissions: AttachedFilesPermissions,
  notificaPP: PropTypes.bool.isRequired,
  idLocation: PropTypes.number
};

LineRowContainerGeneric.defaultProps = {
  setChangeInLines: () => {
    /* empty */
  },
  fetchToApproveALine: () => {
    /* empty */
  },
  deleteTemplateLines: () => {
    /* empty */
  },
  startDate: '',
  disabledInput: false,
  templateComponent: false,
  forceWindow: null,
  asignationType: null,
  isOyM: false,
  maxDate: undefined,
  minDate: undefined,
  monthly: false,
  monthlyPositionLine: false,
  filesPermissions: {
    visualize: false,
    write: false
  },
  idLocation: null
};

export default LineRowContainerGeneric;
